"use client";

import { useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { Field, FormStatus, Input, Select } from "@/components/ui/Form";
import { Badge } from "@/components/ui/Badge/Badge";
import { fetchUserServices, type UserService } from "@/lib/api/c-crm-project-management-user";
import { pluginRouteHref } from "./taskRoute";
import styles from "./CCrmProjectManagement.module.css";

const PAGE_SIZE = 20;

/* Filtr statusu to JEDEN select z dwoma grupami opcji:
   - grupy zbiorcze (`group:*`) - domyslnie "Biezace", czyli to samo co domyslny
     filtr gridu w panelu admina (nowa / w trakcie dostarczania / do aktywacji /
     aktywna / zablokowana). Reszta statusow = nieaktywne.
   - konkretny status (`status:{id}`) - do precyzyjnego zawezenia.
   Jeden control zamiast dwoch nakladajacych sie filtrow (brak stanow sprzecznych). */
const FILTER_CURRENT = "group:current";
const FILTER_INACTIVE = "group:inactive";
const FILTER_ALL = "group:all";
const STATUS_PREFIX = "status:";

const PROJECT_ALL = "all";

/**
 * Zakładka "Usługi" pluginu c_crm_project_management (shell c_user_accounts).
 *
 * Akcja hash-routable (z `sharingContentForApi`): `services`. Zakres NIE jest
 * liczony po właścicielu usługi tylko po uczestnictwie w projekcie, do którego
 * usługa jest przypięta (`IMODEL_M_SERVICES__SERVICES::getUserServices`) - user
 * widzi tu usługi dokładnie tych projektów, które ma w zakładce Projekty.
 *
 * **Domyślny widok = parytet z panelem admina:** usługi bieżące, czyli statusy
 * nowa / w trakcie dostarczania / do aktywacji / aktywna / zablokowana
 * (`M_SERVICES_MCONTROLLER::getActiveServiceStatuses`, flaga `is_current` z API).
 * Pozostałe statusy to usługi nieaktywne - dostępne pod filtrem, nie na wejściu.
 *
 * **Grid:** TanStack Table v8, ten sam wzorzec co `ProjectsList` - cały bounded
 * zbiór pobrany raz, sort/filtr/paginacja client-side. Read-only; przedłużanie
 * i zamawianie usług wejdzie w kolejnej fazie.
 */

/** Y-m-d H:i:s -> Y-m-d (data bez czasu). Defensywnie zwraca wejście gdy brak spacji. */
function formatDate(raw: string | null): string {
  if (!raw) return "";
  return raw.split(" ")[0] || raw;
}

/** Etykieta typu usługi - nazwa ze słownika, a gdy jej brak, sam numer typu. */
function typeLabel(s: UserService): string {
  if (s.type_name) return s.type_name;
  return s.type > 0 ? `Typ ${s.type}` : "-";
}

/** Polska odmiana "dzień/dni" - 1 dzień, 2-4 dni, 5+ dni. */
function dayWord(n: number): string {
  return n === 1 ? "dzień" : "dni";
}

export function ServicesList({ onOpenService }: { onOpenService: (serviceId: number) => void }) {
  const [rows, setRows] = useState<UserService[]>([]);
  const [truncated, setTruncated] = useState(false);
  /* Adres publicznej strony z ofertą pakietów. NULL = domena nie ma takiej podstrony
     (wdrożenie bez sprzedaży online) - wtedy przycisku po prostu nie ma. */
  const [orderHref, setOrderHref] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>(FILTER_CURRENT);
  const [projectFilter, setProjectFilter] = useState<string>(PROJECT_ALL);
  const [sorting, setSorting] = useState<SortingState>([{ id: "expiry_date", desc: false }]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        /* Adres powrotu z panelu przedłużenia - budujemy go TU, bo tylko przeglądarka
           wie, na której domenie CMS jest klient. Backend dokleja go do zaszyfrowanego
           payloadu linku. Bez `window` (SSR) po prostu nie wysyłamy - panel pokaże się
           wtedy bez przycisku powrotu. */
        const returnUrl =
          typeof window !== "undefined"
            ? `${window.location.origin}${window.location.pathname}#extension-services`
            : undefined;
        const { services, truncated: tr, orderHref: oh } = await fetchUserServices(returnUrl);
        if (cancelled) return;
        setRows(services);
        setTruncated(tr);
        setOrderHref(oh);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* Lista projektów do selecta - z faktycznych danych, żeby nie pokazywać projektów
     w których user nie ma żadnej usługi. */
  const projectOptions = useMemo(() => {
    const seen = new Map<number, string>();
    rows.forEach((r) => {
      if (!seen.has(r.id_project)) {
        seen.set(r.id_project, r.project_name ?? `Projekt ${r.id_project}`);
      }
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], "pl"));
  }, [rows]);

  /* Statusy obecne w danych - do drugiej grupy opcji w selecie. Nie pokazujemy
     statusow, ktorych user i tak nie ma w zadnej usludze. */
  const statusOptions = useMemo(() => {
    const seen = new Map<number, string>();
    rows.forEach((r) => {
      if (!seen.has(r.id_status)) {
        seen.set(r.id_status, r.status_name ?? `Status ${r.id_status}`);
      }
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1], "pl"));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (statusFilter === FILTER_CURRENT && !r.is_current) return false;
      if (statusFilter === FILTER_INACTIVE && r.is_current) return false;
      if (
        statusFilter.startsWith(STATUS_PREFIX)
        && String(r.id_status) !== statusFilter.slice(STATUS_PREFIX.length)
      ) {
        return false;
      }
      if (projectFilter !== PROJECT_ALL && String(r.id_project) !== projectFilter) return false;
      return true;
    });
  }, [rows, statusFilter, projectFilter]);

  const hasActiveFilters = statusFilter !== FILTER_CURRENT || projectFilter !== PROJECT_ALL;

  const columns = useMemo(() => {
    const col = createColumnHelper<UserService>();
    return [
      col.accessor("nr", { id: "nr", header: () => "Nr" }),
      col.accessor((r) => typeLabel(r), {
        id: "type",
        header: () => "Usługa",
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <span className={styles.nameCell}>
              <span className={styles.name}>
                {ctx.getValue()}
                {/* Usluga priorytetowa - klient ma widziec, ktora umowa daje mu
                    szybsza obsluge i mozliwosc nadawania priorytetu zgloszeniom. */}
                {r.is_priority && (
                  <span className={styles.priorityBadge}>
                    <Icon name="warning" />
                    Priorytetowa
                  </span>
                )}
              </span>
              {r.package_name && <span className={styles.domain}>{r.package_name}</span>}
              {/* Dodatkowe pola usługi - parametry zdefiniowane w pakiecie i uzupełnione
                  przez operatora przy tej usłudze (np. "Liczba godzin obsługi w miesiącu"
                  dla SLA). Backend przysyła tu tylko te oznaczone jako widoczne dla klienta. */}
              {r.additional_fields?.map((field, i) => (
                <span key={`${i}-${field.name}`} className={styles.additionalField}>
                  {field.name}: <span className={styles.additionalFieldValue}>{field.value}</span>
                </span>
              ))}
            </span>
          );
        },
      }),
      col.accessor((r) => r.project_name ?? "", {
        id: "project",
        header: () => "Projekt",
        cell: (ctx) => ctx.getValue() || "-",
      }),
      col.accessor((r) => r.status_name ?? String(r.id_status), {
        id: "status",
        header: () => "Status",
        cell: (ctx) => {
          const r = ctx.row.original;
          /* Kolor tylko wzmacnia etykiete - tresc statusu jest zawsze w tekscie,
             wiec WCAG 1.4.1 spelnione. */
          const variant = !r.is_current ? "neutral" : r.is_expired ? "warning" : "success";
          return <Badge variant={variant}>{ctx.getValue()}</Badge>;
        },
      }),
      col.accessor((r) => r.add_date, {
        id: "add_date",
        header: () => "Uruchomiona",
        cell: (ctx) => formatDate(ctx.getValue()) || "-",
      }),
      col.accessor((r) => r.expiry_date ?? "", {
        id: "expiry_date",
        header: () => "Wygasa",
        cell: (ctx) => {
          const r = ctx.row.original;
          const value = formatDate(ctx.getValue());
          if (!value) {
            return (
              <span className={styles.nameCell}>
                <span className={styles.stateEnded}>bezterminowo</span>
                {r.resignation && r.resignation_date && (
                  <span className={styles.domain}>
                    rezygnacja: {formatDate(r.resignation_date)}
                  </span>
                )}
              </span>
            );
          }

          //dni po terminie liczy backend (`expiry_days` ujemne) - ta sama reguła
          //co w panelu admina, front tylko formatuje
          const daysAfter = r.expiry_days !== null && r.expiry_days < 0 ? -r.expiry_days : 0;

          return (
            <span className={styles.nameCell}>
              <span className={r.is_expired ? styles.stateStopped : undefined}>{value}</span>

              {daysAfter > 0 && (
                <span className={styles.expiredAgo}>
                  wygasła {daysAfter} {dayWord(daysAfter)} temu
                </span>
              )}

              {/* Ostrzeżenie o usunięciu tylko dla usług PO terminie - przed nim
                  straszenie klienta usunięciem byłoby nie na miejscu. Po karencji
                  komunikat zmienia się z "zostanie usunięta" na stan faktyczny. */}
              {r.is_expired && (
                <span className={styles.deleteWarning}>
                  {r.is_after_grace
                    ? "Okres na przedłużenie minął - usługa przeznaczona do usunięcia."
                    : `Usługa zostanie usunięta po ${formatDate(r.grace_until)} - przedłuż ją, aby jej nie stracić.`}
                </span>
              )}

              {/* Rezygnacja jest ortogonalna do statusu - usługa bywa jeszcze
                  aktywna, a rezygnacja już złożona (działa do końca okresu). */}
              {r.resignation && (
                <span className={styles.domain}>
                  {r.resignation_date
                    ? `rezygnacja: ${formatDate(r.resignation_date)}`
                    : "złożono rezygnację"}
                </span>
              )}
            </span>
          );
        },
      }),
      col.display({
        id: "actions",
        header: () => <span className="sr-only">Akcje</span>,
        cell: (ctx) => {
          const r = ctx.row.original;
          /* Link, nie przycisk - panel usługi ma własny adres, więc klient może
             otworzyć go w nowej karcie albo przesłać dalej. Zwykłe kliknięcie
             przejmujemy (zmiana hasha nie przeładowuje Server Components),
             modyfikatory zostawiamy przeglądarce. */
          const intercept = (go: () => void) => (e: React.MouseEvent) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            go();
          };
          return (
            <span className={styles.rowActions}>
              <a
                className={styles.rowButton}
                href={pluginRouteHref({ view: "service", serviceId: r.id })}
                onClick={intercept(() => onOpenService(r.id))}
                title={`Panel usługi ${r.nr}`}
              >
                <Icon name="gear" />
                Panel usługi
              </a>
              {/* Link przedłużenia pojawia się tylko gdy backend uzna je za możliwe
                  wg dat i konfiguracji pakietu (`isRenewLinkAvailable`). Prowadzi do
                  panelu przedłużenia w domenie backendu - id usługi jest zaszyfrowane
                  w adresie, więc klient nie musi się logować. Stąd zwykła nawigacja,
                  bez proxy. */}
              {r.renew_href && (
                <a
                  href={r.renew_href}
                  className={styles.renewBtn}
                  title={`Przedłuż usługę ${r.nr}`}
                  aria-label={`Przedłuż usługę ${r.nr}`}
                >
                  <Icon name="arrow-clockwise" className={styles.renewIcon} />
                  Przedłuż
                </a>
              )}
            </span>
          );
        },
      }),
      /* Kolumny "Stan" celowo NIE ma - status usługi już ją niesie (Aktywna /
         Zablokowana / Rezygnacja z usługi ...), a druga kolumna z tym samym
         znaczeniem tylko myliła. Sygnały ortogonalne do statusu (po dacie
         ważności, złożona rezygnacja) są przy dacie wygaśnięcia. */
    ];
  }, [onOpenService]);

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className={styles.root}>
      <div className={styles.listIntro}>
        <p className={styles.note}>
          Usługi z projektów w których uczestniczysz. Domyślnie widzisz usługi bieżące -
          nowe, w trakcie dostarczania, do aktywacji, aktywne i zablokowane.
        </p>
        {/* Wejście na publiczną stronę z ofertą pakietów. Zwykły `<a>` - to inna
            podstrona CMS, a nie akcja rozszerzenia w hashu tego panelu. */}
        {orderHref && (
          <a className={styles.orderLink} href={orderHref}>
            <Icon name="plus" />
            Zamów usługę
          </a>
        )}
      </div>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <Field label="Status" className={styles.filterField}>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value={FILTER_CURRENT}>Bieżące</option>
              <option value={FILTER_INACTIVE}>Nieaktywne</option>
              <option value={FILTER_ALL}>Wszystkie</option>
              {statusOptions.length > 1 && (
                <optgroup label="Konkretny status">
                  {statusOptions.map(([value, label]) => (
                    <option key={value} value={`${STATUS_PREFIX}${value}`}>
                      {label}
                    </option>
                  ))}
                </optgroup>
              )}
            </Select>
          </Field>
          {projectOptions.length > 1 && (
            <Field label="Projekt" className={styles.filterField}>
              <Select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                <option value={PROJECT_ALL}>Wszystkie</option>
                {projectOptions.map(([value, label]) => (
                  <option key={value} value={String(value)}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {hasActiveFilters && (
            <button
              type="button"
              className={styles.clearFilters}
              onClick={() => {
                setStatusFilter(FILTER_CURRENT);
                setProjectFilter(PROJECT_ALL);
              }}
            >
              <Icon name="x" />
              Wyczyść filtry
            </button>
          )}
        </div>
        <div className={styles.searchWrap}>
          <Input
            type="search"
            prefix={<Icon name="magnifying-glass" />}
            placeholder="Szukaj (nr, usługa, projekt, status)..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Szukaj usług"
          />
        </div>
      </div>

      {loading && <FormStatus variant="info">Wczytywanie usług...</FormStatus>}
      {error && !loading && <FormStatus variant="error">{error}</FormStatus>}

      {!loading && !error && rows.length === 0 && (
        <FormStatus variant="info">
          W Twoich projektach nie uruchomiono jeszcze żadnej usługi.
        </FormStatus>
      )}

      {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
        <FormStatus variant="info">Brak usług spełniających wybrane filtry.</FormStatus>
      )}

      {!loading && !error && filteredRows.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={canSort ? styles.thSortable : undefined}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sorted === "asc"
                              ? "ascending"
                              : sorted === "desc"
                                ? "descending"
                                : undefined
                          }
                        >
                          <span className={styles.thInner}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <Icon
                                name={sorted === "desc" ? "caret-down" : "caret-up"}
                                className={sorted ? styles.sortIconActive : styles.sortIcon}
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {truncated && (
            <p className={styles.truncatedHint}>
              Wyświetlono pierwsze 500 usług. Zawęź wyszukiwanie aby znaleźć pozostałe.
            </p>
          )}

          {pageCount > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon name="caret-left" />
                Poprzednia
              </button>
              <span className={styles.pageInfo}>
                Strona {pageIndex + 1} z {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Następna
                <Icon name="caret-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
